import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ApplicationBaseData, BrandingService, CompanyBranding, CompanyBrandingHelper } from 'common';
import {
  getOfferPageStatusData,
  OFFER_PAGE_FALLBACK_COMPANY_NAME,
  OFFER_PAGE_FALLBACK_EMAIL,
  OFFER_PAGE_FALLBACK_PHONE,
  OfferPageStatusData
} from './offer-page-status.data';
import { OfferPageScreen } from './offer-page-status.model';
import { LocalSettings } from '../../shared/local.settings';

@UntilDestroy()
@Component({
    selector: 'ifa-offer-page-status-message',
    templateUrl: './offer-page-status-message.component.html',
    styleUrls: ['./offer-page-status-message.component.scss'],
    standalone: false
})
export class OfferPageStatusMessageComponent implements OnInit, OnChanges {
  @Input() screen: OfferPageScreen;
  @Input() application: ApplicationBaseData;

  readonly fallbackEmail = OFFER_PAGE_FALLBACK_EMAIL;

  data: OfferPageStatusData;
  companyName: string;
  // The advisor's own number when one is assigned and the brand allows it, otherwise the company
  // number — the same value the app bar shows top right.
  contactPhone: string;
  showAdvisorContact: boolean;
  applyUrl: string;
  canApply: boolean;

  private branding: CompanyBranding;

  constructor(
    private readonly brandingService: BrandingService,
    private readonly localSettings: LocalSettings
  ) { }

  ngOnInit(): void {
    this.brandingService.getBranding$()
      .pipe(untilDestroyed(this))
      .subscribe(branding => {
        this.branding = branding;
        this.refresh();
      });
  }

  ngOnChanges(): void {
    this.refresh();
  }

  // Everything the template binds is precomputed here — templates must call no function and no
  // getter (applicationFormRedirection is a getter).
  private refresh(): void {
    const redirection = this.localSettings.applicationFormRedirection;

    // Defaulted because branding is a separate async source: without it the Declined body renders
    // the literal string "undefined" while the request is in flight, or forever if it fails.
    this.companyName = this.branding?.dba ?? OFFER_PAGE_FALLBACK_COMPANY_NAME;
    this.data = getOfferPageStatusData(this.screen, this.companyName);
    // advisorName interpolates two nulls into a single space server-side, so it is truthy even
    // when no advisor is assigned — gate on the id.
    this.showAdvisorContact = this.application?.advisorId != null;
    // With no advisor the number is the company one — the same value AppBarContactComponent shows
    // top right, including its final hardcoded fallback. Resolved from the gate, not from
    // advisorPhone, so a stray advisorPhone on an unassigned application can never leak a personal
    // number into the "Call us at" sentence. Both branches default, or the sentence renders a gap
    // and an href of "tel:undefined" until branding lands.
    const companyPhone = this.branding?.phone ?? OFFER_PAGE_FALLBACK_PHONE;
    this.contactPhone = this.showAdvisorContact
      ? CompanyBrandingHelper.getAdvisorPhone(this.branding, this.application?.advisorPhone) ?? companyPhone
      : companyPhone;
    this.applyUrl = redirection?.url;
    this.canApply = redirection?.allowed === true && !!redirection?.url;
  }
}
